import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import { adminUpdateOrderMeal } from 'graphql/mutations';
import { request } from 'utilities/graph';

const useStyles = makeStyles({
  table: {
    minWidth: 500,
  },
  dateField: {
    marginTop: 16,
    marginLeft: 8,
    width: 150,
  },
  note: {
    marginTop: 16,
    marginLeft: 8,
  },
});

export default function UpdateOrderMealDialog({
  open,
  setOpen,
  restaurantId,
  oldMeal,
  newMeal,
}) {
  const classes = useStyles();
  const [effectiveDate, setEffectiveDate] = useState(moment().format('YYYY-MM-DD'));
  const [isLoading, setIsLoading] = useState(false);
  const [effectiveDateError, setEffectiveDateError] = useState('');

  const handleClose = () => {
    setEffectiveDate(moment().format('YYYY-MM-DD'));
    setEffectiveDateError('');
    setOpen(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (!window.confirm('確認要修改週期性排單和訂單嗎？')) {
      setIsLoading(false);
      throw new Error('abort');
    }

    const input = {
      restaurantId,
      oldMealName: oldMeal.name,
      newMealName: newMeal.name,
      newMealCost: newMeal.cost,
      newMealPrice: newMeal.price,
      newMealSurcharges: newMeal.surcharges,
      effectiveDate: effectiveDate,
    };

    try {
      await request(adminUpdateOrderMeal, { input });
    } catch (e) {
      console.log(e);
    }

    setIsLoading(false);
    handleClose();
  };

  const today = moment().startOf('day');
  const minDate = moment(today).subtract(30, 'days');
  const maxDate = moment(today).add(30, 'days');

  return (
    <Fragment>
      <Dialog
        open={open}
        onClose={(e, reason)=>{
          if (reason === 'escapeKeyDown' || reason === 'backdropClick') return;
          handleClose();
        }}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          修改週期性排單與訂單
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table className={classes.table} size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>項目</TableCell>
                  <TableCell>修改前</TableCell>
                  <TableCell>修改後</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { label: '名稱', key: 'name' },
                  { label: '單價', key: 'price' },
                  { label: '進價', key: 'cost' },
                ].map(({ label, key }) => (
                  <TableRow key={key}>
                    <TableCell>{label}</TableCell>
                    <TableCell>{oldMeal[key]}</TableCell>
                    <TableCell>{newMeal[key]}</TableCell>
                  </TableRow>
                ))}
                {(oldMeal.surcharges?.length > 0 || newMeal.surcharges?.length > 0) && (
                  <TableRow key='surcharges'>
                    <TableCell>額外成本</TableCell>
                    <TableCell>
                      {oldMeal.surcharges?.map((surcharge) => `${surcharge.name} ${surcharge.price}`).join('、')}
                    </TableCell>
                    <TableCell>
                      {newMeal.surcharges?.map((surcharge) => `${surcharge.name} ${surcharge.price}`).join('、')}
                    </TableCell>
                  </TableRow>)
                }
              </TableBody>
            </Table>
          </TableContainer>
          <TextField
            id="start-date"
            className={classes.dateField}
            label={'餐點修改生效日期'}
            type="date"
            value={effectiveDate}
            disabled={isLoading}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: minDate.format('YYYY-MM-DD'),
              max: maxDate.format('YYYY-MM-DD'),
            }}
            onChange={(e) => {
              const selectedDate = moment(e.target.value, 'YYYY-MM-DD');

              if (!selectedDate.isValid() || selectedDate.isBefore(minDate) || selectedDate.isAfter(maxDate)) {
                setEffectiveDateError('請選擇過去30天內至未來30天內的日期');
              } else {
                setEffectiveDateError('');
              }
              setEffectiveDate(e.target.value);
            }}
            error={!!effectiveDateError}
            helperText={effectiveDateError}
          />
          <div className={classes.note}>
            <Typography variant="body1">
              請注意：
            </Typography>
            <Typography variant="body2">
              {`1. 週期性排單中若餐點名稱為『${oldMeal.name}』將被修改為新的餐點設定。`}
            </Typography>
            <Typography variant="body2">
              {`2. 訂單中送餐時間為${effectiveDate}當天或之後，且餐點名稱為『${oldMeal.name}』將被修改為新的餐點設定。`}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" disabled={isLoading}>
            取消
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isLoading || !!effectiveDateError}>
            確定
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

UpdateOrderMealDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  restaurantId: PropTypes.string,
  oldMeal: PropTypes.object,
  newMeal: PropTypes.object,
};
